import { PaperClipOutlined } from "@ant-design/icons";
import { Body } from "@indigo-components";
import { useSetAtom } from "jotai";
import { FC, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import type { Components } from "react-markdown";
import type { SkillMenuItem } from "~/atoms/chatbot";
import { dictationSurfaceIntentAtom } from "~/atoms/dictation";
import { type ChatContext } from "~/hooks/useChatbotContext";
import { zIndex } from "~/indigo-components/src/styles/tokens";
import type { ChatbotLinkContext } from "~/pages/appointments/components/chatbotLinks";
import type { UseAgentChatReturn } from "~/pages/appointments/hooks/useAgentChat";
import { ChatComposer } from "./ChatComposer";
import { ConversationSidebar } from "./ConversationSidebar";
import "./IndyChatSurface.css";
import { MessageList } from "./MessageList";

export interface IndyChatSurfaceProps {
  /** "panel" = floating ChatbotModal; "fullscreen" = dedicated /indy page. */
  layout: "panel" | "fullscreen";
  /** Whether the surface is active/visible — gates one-shot composer effects. */
  active: boolean;
  context: ChatContext | null;
  chat: UseAgentChatReturn;
  availableSkills: SkillMenuItem[];
  /** `refreshIfStale` from useAvailableSkills. */
  refreshAvailableSkills: () => void;
  markdownComponents: Components;
  linkContext: ChatbotLinkContext;
  userGreetingName: string | null;
  /**
   * Panel: host-controlled foldable rail state (driven by the title-bar
   * toggle). Fullscreen: ignored unless `mobile`, where it drives the
   * off-canvas conversation drawer.
   */
  sidebarOpen: boolean;
  /**
   * Fullscreen only: phone-width layout — the conversation rail becomes an
   * off-canvas drawer over the chat instead of a permanent 280px column, which
   * at 375px would leave the chat unusable. Panel layout ignores it.
   */
  mobile?: boolean;
  /** Fullscreen + mobile: dismiss the conversation drawer (backdrop tap). */
  onSidebarClose?: () => void;
  /** Rendered at the top of the surface: panel title bar or `IndyHeader`. */
  header: ReactNode;
}

/**
 * Presentational chat surface shared by the floating panel and the full-screen
 * page. Owns the message input value (shared between the composer and empty-
 * state suggestions) and the drag-to-attach overlay; everything else is
 * delegated to `ConversationSidebar`, `MessageList`, and `ChatComposer`. The
 * host owns the `useAgentChat` hook and passes its return in via `chat`.
 */
export const IndyChatSurface: FC<IndyChatSurfaceProps> = ({
  layout,
  active,
  context,
  chat,
  availableSkills,
  refreshAvailableSkills,
  markdownComponents,
  linkContext,
  userGreetingName,
  sidebarOpen,
  mobile = false,
  onSidebarClose,
  header,
}) => {
  const fullscreen = layout === "fullscreen";
  const drawerNav = fullscreen && mobile;
  const [inputValue, setInputValue] = useState("");

  // Claim the preconnected dictation session while this surface is open. The
  // singleton holds one session and an Assessment-shaped one cannot serve Indy
  // — without this the warm connection is torn down and renegotiated on the
  // first mic press, which is exactly the delay preconnecting exists to remove.
  const setDictationSurfaceIntent = useSetAtom(dictationSurfaceIntentAtom);
  useEffect(() => {
    if (!active) return;
    setDictationSurfaceIntent("indy");
    return () => setDictationSurfaceIntent(null);
  }, [active, setDictationSurfaceIntent]);

  // ── Drag-and-drop attachments (covers the whole chat area) ──
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);

  // While the drawer is open it covers the surface, and the scrim only stops
  // pointers — Tab would still walk behind it: into the composer (typing goes
  // to an invisible input) and on to the header, where New chat would start a
  // conversation the user cannot see. `inert` takes both regions out of the tab
  // order and the a11y tree. Set imperatively because React 18 has no `inert`
  // prop (React 19 does).
  const headerRef = useRef<HTMLDivElement>(null);
  const chatAreaRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const surfaceInert = drawerNav && sidebarOpen;
  useEffect(() => {
    const nodes = [headerRef.current, chatAreaRef.current].filter(
      (n): n is HTMLDivElement => n !== null,
    );
    if (surfaceInert) {
      // Making an ancestor inert blurs whatever is focused inside it, so the
      // composer would silently lose the caret across an open/close cycle.
      const active = document.activeElement;
      restoreFocusRef.current =
        active instanceof HTMLElement && nodes.some((n) => n.contains(active))
          ? active
          : null;
      nodes.forEach((n) => n.setAttribute("inert", ""));
      return;
    }
    nodes.forEach((n) => n.removeAttribute("inert"));
    restoreFocusRef.current?.focus();
    restoreFocusRef.current = null;
  }, [surfaceInert]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.types.includes("Files")) setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounterRef.current = 0;
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        Array.from(files).forEach((file) => void chat.addAttachment(file));
      }
    },
    [chat],
  );

  return (
    <div
      className="indy-chat-surface flex flex-col"
      style={{
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        ...(fullscreen
          ? {
              // Fills `FullPageLayout`, which owns the viewport height (and
              // sizes itself in `dvh` so the mobile URL bar is accounted for).
              height: "100%",
              width: "100%",
              backgroundColor: "var(--indigo-gray-25, #fff)",
            }
          : {}),
      }}
    >
      {/* Wrapped so the drawer can mark the header inert alongside the chat;
          `IndyHeader` owns its own `flexShrink`, which this must not lose. */}
      <div ref={headerRef} style={{ flexShrink: 0 }}>
        {header}
      </div>

      {/* Body: sidebar + chat area */}
      <div className="flex flex-1 overflow-hidden" style={{ minHeight: 0 }}>
        {/* Drawer scrim. Rendered before the sidebar so DOM order — not a
            magic z-index — puts the drawer on top of it. */}
        {drawerNav && sidebarOpen && (
          <div
            className="fixed inset-0"
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.32)",
              zIndex: zIndex.layout,
            }}
            onClick={onSidebarClose}
            aria-hidden
          />
        )}
        <ConversationSidebar
          variant={drawerNav ? "drawer" : fullscreen ? "rail" : "foldable"}
          open={sidebarOpen}
          chat={chat}
          onDismiss={drawerNav ? onSidebarClose : undefined}
        />

        {/* Chat area */}
        <div
          ref={chatAreaRef}
          className="relative flex flex-col overflow-hidden"
          style={{ flex: 1, minWidth: 0 }}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {/* Drop overlay */}
          {isDragging && (
            <div
              className="absolute inset-3 z-50 flex flex-col items-center justify-center gap-2 rounded-xl"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                border: "2px dashed var(--indigo-gray-300)",
              }}
            >
              <PaperClipOutlined
                style={{ fontSize: 20, color: "var(--indigo-text-tertiary)" }}
              />
              <Body
                as="span"
                size="sm"
                style={{
                  fontWeight: 500,
                  color: "var(--indigo-text-tertiary)",
                  letterSpacing: "0.01em",
                }}
              >
                Drop to attach
              </Body>
            </div>
          )}

          <MessageList
            layout={layout}
            mobile={drawerNav}
            chat={chat}
            context={context}
            userGreetingName={userGreetingName}
            markdownComponents={markdownComponents}
            linkContext={linkContext}
            onSelectSuggestion={setInputValue}
          />

          <ChatComposer
            layout={layout}
            mobile={drawerNav}
            active={active}
            chat={chat}
            availableSkills={availableSkills}
            refreshAvailableSkills={refreshAvailableSkills}
            value={inputValue}
            onValueChange={setInputValue}
          />
        </div>
      </div>
    </div>
  );
};
